import React, { useState, useEffect, useContext } from 'react';
import { 
  ShieldCheckIcon,
  EyeIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  UserIcon,
  XCircleIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { AuthContext } from '../App';
import ConfirmationModal from '../components/ConfirmationModal';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface PrivacyRequest {
  reference_number: string;
  request_type: 'data-access' | 'delete-account';
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  created_at: string;
  member_email?: string;
  notes?: string;
}

interface Statistics {
  total_count: number;
  pending_count: number;
  processing_count: number;
  completed_count: number;
  data_access_count: number;
  deletion_count: number;
}

interface MemberData {
  personal_data?: any;
  dogs?: any[];
  calendar_bookings?: any[];
  calendar_events_as_trainer?: any[];
  drinks?: any[];
  guest_cards?: any[];
  guest_card_bookings?: any[];
  roles?: any[];
  documents?: any[];
  finance_bookings?: any[];
  finance_details?: any[];
  paypal_payments?: any[];
  notifications?: any;
  planning?: any[];
  news?: any[];
  member_audits?: any[];
  audit_logs?: any[];
  auth_logs?: any[];
  privacy_consents?: any[];
}

const PrivacyManagement: React.FC = () => {
  const { BASE_URL } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState<'requests' | 'statistics' | 'data-export'>('requests');
  const [requests, setRequests] = useState<PrivacyRequest[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<PrivacyRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [memberDataEmail, setMemberDataEmail] = useState('');
  const [memberData, setMemberData] = useState<MemberData | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lade Privacy-Anfragen
  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/privacy/requests');
      setRequests(response.data.requests || []);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Laden der Anfragen',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  // Lade Statistiken
  const loadStatistics = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/privacy/statistics');
      setStatistics(response.data.statistics);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Laden der Statistiken',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  // Lade Details einer Anfrage
  const loadRequestDetails = async (reference: string) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/privacy/requests/${reference}`);
      setSelectedRequest(response.data.request);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Laden der Details',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  // Status einer Anfrage aktualisieren
  const updateRequestStatus = async (reference: string, status: string, notes: string) => {
    setLoading(true);
    try {
      await apiClient.put(`/api/privacy/requests/${reference}/status`, {
        status,
        notes
      });
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Status wurde erfolgreich aktualisiert',
        variant: 'single'
      });
      loadRequests();
      if (selectedRequest) {
        loadRequestDetails(reference);
      }
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Aktualisieren',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  // Löschung lokal durchführen
  const processLocalDeletion = async (memberEmail: string, reference: string) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Löschung bestätigen',
      message: `WARNUNG: Dies wird alle Daten von ${memberEmail} unwiderruflich löschen! Sind Sie sicher?`,
      variant: 'two',
      onConfirm: async () => {
        setLoading(true);
        try {
          const response = await apiClient.post('/api/privacy/process-deletion', {
            member_email: memberEmail,
            reference
          });
          
          if (response.data.success) {
            // Bestätige beim Discovery Service
            await apiClient.post(`/api/privacy/requests/${reference}/confirm-deletion`, {
              confirmation_text: 'Alle Daten wurden gemäß DSGVO Art. 17 gelöscht',
              deleted_data_summary: JSON.stringify(response.data.deletion_summary),
              member_email: memberEmail
            });
            
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Erfolgreich',
              message: 'Löschung wurde erfolgreich durchgeführt und bestätigt',
              variant: 'single'
            });
            loadRequests();
          }
        } catch (err: any) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: err.response?.data?.message || 'Fehler bei der Löschung',
            variant: 'single'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Mitgliedsdaten für Dateneinsicht laden
  const loadMemberData = async () => {
    if (!memberDataEmail) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Bitte E-Mail-Adresse eingeben',
        variant: 'single'
      });
      return;
    }

    setLoading(true);
    setMemberData(null);
    try {
      const response = await apiClient.get(`/api/privacy/member-data/${memberDataEmail}`);
      setMemberData(response.data.data);
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Daten wurden erfolgreich geladen',
        variant: 'single'
      });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Laden der Mitgliedsdaten',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  // PDF Export Funktion
  const exportToPDF = () => {
    if (!memberData || !memberData.personal_data) return;

    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    let yPosition = 20;

    // Helper-Funktion für neue Seite
    const checkNewPage = (neededSpace = 20) => {
      if (yPosition + neededSpace > pageHeight - 20) {
        doc.addPage();
        yPosition = 20;
        return true;
      }
      return false;
    };

    // Titel
    doc.setFontSize(20);
    doc.text('Dateneinsicht gemäß DSGVO Art. 15', 20, yPosition);
    yPosition += 10;

    // Generierungsdatum
    doc.setFontSize(10);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')} um ${new Date().toLocaleTimeString('de-DE')}`, 20, yPosition);
    yPosition += 10;

    // E-Mail
    doc.text(`Mitglied: ${memberDataEmail}`, 20, yPosition);
    yPosition += 15;

    // Persönliche Daten
    if (memberData.personal_data) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('1. Persönliche Daten', 20, yPosition);
      doc.setFont('helvetica', 'normal');
      yPosition += 10;

      const personalData = memberData.personal_data;
      const personalInfo = [
        ['Name', `${personalData.vorname || ''} ${personalData.name || ''}`],
        ['Mitgliedsnummer', personalData.mitglied_nr || '-'],
        ['E-Mail', personalData.email || '-'],
        ['Telefon', personalData.tel_nummer || '-'],
        ['Adresse', `${personalData.strasse || ''}, ${personalData.plz || ''} ${personalData.ort || ''}`],
        ['Geburtsdatum', personalData.geb_am || '-'],
        ['Eintritt', personalData.eintritt || '-'],
        ['Status', personalData.active ? 'Aktiv' : 'Inaktiv']
      ];

      (doc as any).autoTable({
        startY: yPosition,
        head: [['Feld', 'Wert']],
        body: personalInfo,
        theme: 'grid',
        styles: { fontSize: 9 }
      });
      yPosition = (doc as any).lastAutoTable.finalY + 10;
    }

    // Weitere Abschnitte können hier hinzugefügt werden...

    // Speichern
    doc.save(`dateneinsicht_${memberDataEmail}_${Date.now()}.pdf`);
    setConfirmModal({
      isOpen: true,
      type: 'success',
      title: 'Erfolgreich',
      message: 'PDF wurde erfolgreich heruntergeladen',
      variant: 'single'
    });
  };

  // Export als JSON
  const exportToJSON = () => {
    if (!memberData) return;

    const dataStr = JSON.stringify(memberData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `dateneinsicht_${memberDataEmail}_${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    setConfirmModal({
      isOpen: true,
      type: 'success',
      title: 'Erfolgreich',
      message: 'JSON wurde erfolgreich heruntergeladen',
      variant: 'single'
    });
  };

  // Initial laden
  useEffect(() => {
    if (activeTab === 'requests') {
      loadRequests();
    } else if (activeTab === 'statistics') {
      loadStatistics();
    }
  }, [activeTab]);

  // Status Badge Component
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const badges = {
      pending: { 
        className: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300', 
        icon: <ClockIcon className="h-3 w-3" />, 
        text: 'Ausstehend' 
      },
      processing: { 
        className: 'bg-info-100 text-info-800 dark:bg-info-900/20 dark:text-info-300', 
        icon: <ArrowPathIcon className="h-3 w-3 animate-spin" />, 
        text: 'In Bearbeitung' 
      },
      completed: { 
        className: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300', 
        icon: <CheckCircleIcon className="h-3 w-3" />, 
        text: 'Abgeschlossen' 
      },
      rejected: { 
        className: 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-300', 
        icon: <XCircleIcon className="h-3 w-3" />, 
        text: 'Abgelehnt' 
      }
    };

    const badge = badges[status as keyof typeof badges] || badges.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badge.className}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  // Request Type Badge
  const TypeBadge: React.FC<{ type: string }> = ({ type }) => {
    const types = {
      'data-access': { 
        className: 'bg-info-100 text-info-800 dark:bg-info-900/20 dark:text-info-300', 
        icon: <EyeIcon className="h-3 w-3" />, 
        text: 'Dateneinsicht' 
      },
      'delete-account': { 
        className: 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-300', 
        icon: <TrashIcon className="h-3 w-3" />, 
        text: 'Löschung' 
      }
    };

    const badge = types[type as keyof typeof types] || types['data-access'];
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badge.className}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header - Variante 1: Einfacher Header */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">DSGVO-Verwaltung</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Verwaltung von Datenschutz-Anfragen gemäß DSGVO Art. 15 & 17</p>
            </div>
          </div>
        </div>
      </header>

      {/* Statistics Grid */}
      {activeTab === 'statistics' && statistics && (
        <StatGroup columns={6} className="mb-6">
          <StatCard icon={ChartBarIcon} tone="neutral" label="Gesamt" value={statistics.total_count || 0} />
          <StatCard icon={ClockIcon} tone="warning" valueTone="warning" label="Ausstehend" value={statistics.pending_count || 0} />
          <StatCard icon={ArrowPathIcon} tone="info" valueTone="info" label="In Bearbeitung" value={statistics.processing_count || 0} />
          <StatCard icon={CheckCircleIcon} tone="success" valueTone="success" label="Abgeschlossen" value={statistics.completed_count || 0} />
          <StatCard icon={EyeIcon} tone="info" valueTone="info" label="Dateneinsicht" value={statistics.data_access_count || 0} />
          <StatCard icon={TrashIcon} tone="error" valueTone="error" label="Löschungen" value={statistics.deletion_count || 0} />
        </StatGroup>
      )}

      {/* Tabs */}
      <div className="mb-6 grid grid-cols-3 gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
        <button
          onClick={() => setActiveTab('requests')}
          className={`min-w-0 rounded-md px-2 py-2 text-xs font-medium transition-colors sm:px-4 sm:text-base ${
            activeTab === 'requests'
              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Anfragen
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={`min-w-0 rounded-md px-2 py-2 text-xs font-medium transition-colors sm:px-4 sm:text-base ${
            activeTab === 'statistics'
              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Statistiken
        </button>
        <button
          onClick={() => setActiveTab('data-export')}
          className={`min-w-0 rounded-md px-2 py-2 text-xs font-medium transition-colors sm:px-4 sm:text-base ${
            activeTab === 'data-export'
              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Dateneinsicht
        </button>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {/* Tab: Anfragen */}
        {activeTab === 'requests' && (
          <div>
            <div className="mb-4">
              <button 
                onClick={loadRequests} 
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <ArrowPathIcon className="h-5 w-5" />
                Aktualisieren
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Keine offenen Privacy-Anfragen vorhanden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((request) => (
                  <div key={request.reference_number} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{request.reference_number}</h4>
                        <div className="flex gap-2 mt-2">
                          <TypeBadge type={request.request_type} />
                          <StatusBadge status={request.status} />
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(request.created_at).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <button 
                        onClick={() => loadRequestDetails(request.reference_number)}
                        className="px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm"
                      >
                        Details
                      </button>
                      
                      {request.status === 'pending' && (
                        <button 
                          onClick={() => updateRequestStatus(request.reference_number, 'processing', 'Bearbeitung gestartet')}
                          className="px-3 py-1 bg-info-600 text-white rounded hover:bg-info-700 transition-colors text-sm"
                        >
                          Bearbeiten
                        </button>
                      )}
                      
                      {request.status === 'processing' && request.request_type === 'delete-account' && (
                        <button 
                          onClick={() => {
                            const email = prompt('E-Mail-Adresse des Mitglieds:');
                            if (email) {
                              processLocalDeletion(email, request.reference_number);
                            }
                          }}
                          className="px-3 py-1 bg-error-600 text-white rounded hover:bg-error-700 transition-colors text-sm"
                        >
                          Löschung durchführen
                        </button>
                      )}
                    </div>

                    {selectedRequest?.reference_number === request.reference_number && (
                      <div className="mt-4 p-4 bg-white dark:bg-gray-600 rounded-lg">
                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">Details:</h5>
                        <pre className="text-xs text-gray-600 dark:text-gray-300 overflow-auto">
                          {JSON.stringify(selectedRequest, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab: Statistiken */}
        {activeTab === 'statistics' && (
          <div className="text-center py-12">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Statistiken werden oben in den Karten angezeigt
            </p>
          </div>
        )}

        {/* Tab: Dateneinsicht */}
        {activeTab === 'data-export' && (
          <div>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Mitgliedsdaten für Dateneinsicht abrufen
              </h3>
              <div className="flex gap-4">
                <div className="flex-1">
                  <AttributeLabel iconKey="email" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                    E-Mail-Adresse des Mitglieds:
                  </AttributeLabel>
                  <input
                    type="email"
                    value={memberDataEmail}
                    onChange={(e) => setMemberDataEmail(e.target.value)}
                    placeholder="mitglied@example.com"
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="flex items-end">
                  <button 
                    onClick={loadMemberData}
                    disabled={loading || !memberDataEmail}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                    Daten laden
                  </button>
                </div>
              </div>
            </div>

            {memberData && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Dateneinsicht für: {memberDataEmail}
                  </h3>
                  <div className="flex gap-2">
                    <button 
                      onClick={exportToJSON}
                      className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors flex items-center gap-2"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                      JSON Export
                    </button>
                    <button 
                      onClick={exportToPDF}
                      className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors flex items-center gap-2"
                    >
                      <DocumentArrowDownIcon className="h-5 w-5" />
                      PDF Export
                    </button>
                  </div>
                </div>
                
                {/* Data Sections */}
                {memberData.personal_data && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <UserIcon className="h-5 w-5" />
                      Persönliche Daten
                    </h4>
                    <div className="max-h-60 overflow-y-auto">
                      <pre className="text-sm text-gray-600 dark:text-gray-300 overflow-auto bg-white dark:bg-gray-800 p-3 rounded">
                        {JSON.stringify(memberData.personal_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Weitere Sections können hier hinzugefügt werden */}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'single'}
        showCloseButton={true}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm?.();
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          }
        } : undefined}
      />
    </div>
  );
};

export default PrivacyManagement;
